/**
 * Predictive Search - Course Style
 * Customiza os resultados de busca para exibir cursos com foto + nome + preço
 */

class PredictiveSearchCourse extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.searchTerm = '';
    
    // Configuração de cores por coleção (do Theme Settings)
    this.badgeColors = window.badgeColors || {};
    this.defaultBadgeColor = window.defaultBadgeColor || '#FF6B00';
  }

  connectedCallback() {
    this.input.addEventListener('input', this.debounce((event) => {
      this.onChange(event);
    }, 300));
  }

  onChange(event) {
    const searchTerm = event.target.value.trim();
    
    if (searchTerm === '') {
      this.close();
      return;
    }

    this.searchTerm = searchTerm;
    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    const url = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=8&section_id=predictive-search`;
    
    fetch(url)
      .then(response => response.json())
      .then(data => {
        this.renderSearchResults(data);
      })
      .catch(error => {
        console.error('Search error:', error);
        this.close();
      });
  }

  renderSearchResults(results) {
    const products = results.resources.results.products || [];
    
    if (products.length === 0) {
      this.renderEmpty();
      return;
    }

    // Limpa resultados anteriores
    this.predictiveSearchResults.innerHTML = '';
    
    // Cria container
    const container = document.createElement('div');
    container.id = 'predictive-search-results';
    container.setAttribute('role', 'listbox');
    
    // Adiciona heading
    const heading = document.createElement('h2');
    heading.className = 'predictive-search__heading';
    heading.textContent = 'Cursos';
    container.appendChild(heading);
    
    // Cria lista
    const ul = document.createElement('ul');
    ul.className = 'predictive-search__results-list';
    
    // Renderiza cada produto
    products.forEach(product => {
      const li = this.createProductItem(product);
      ul.appendChild(li);
    });
    
    container.appendChild(ul);
    
    // Adiciona link "Ver todos os resultados"
    const viewAllLink = document.createElement('a');
    viewAllLink.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(this.searchTerm)}`;
    viewAllLink.className = 'predictive-search__view-all';
    viewAllLink.textContent = `Ver todos os resultados para "${this.searchTerm}"`;
    container.appendChild(viewAllLink);
    
    this.predictiveSearchResults.appendChild(container);
    this.open();
  }

  createProductItem(product) {
    const li = document.createElement('li');
    li.className = 'predictive-search__list-item';
    
    // Determina a cor do badge
    const badgeColor = this.getBadgeColor(product);
    const collectionTitle = product.collections && product.collections.length > 0 
      ? product.collections[0] 
      : 'Curso';
    
    // Formata o preço
    const price = this.formatMoney(product.price);
    
    // Cria o link
    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'predictive-search__item';
    
    link.innerHTML = `
      <div class="predictive-search__item-image-wrapper">
        ${product.image ? `
          <img 
            src="${product.image}" 
            alt="${this.escapeHtml(product.title)}"
            class="predictive-search__item-image"
            loading="lazy"
            width="80"
            height="60"
          >
        ` : `
          <div class="predictive-search__item-image" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);"></div>
        `}
        
      </div>
      
      <div class="predictive-search__item-content">
        <h3 class="predictive-search__item-heading">
          ${this.escapeHtml(product.title)}
        </h3>
        
        
        
        <div class="predictive-search__item-price">
          <span class="predictive-search__item-price--current">${price}</span>
        </div>
      </div>
    `;
    
    li.appendChild(link);
    return li;
  }

  getBadgeColor(product) {
    // Se o produto tem coleções, tenta pegar a cor configurada
    if (product.collections && product.collections.length > 0) {
      const collectionHandle = product.collections[0].toLowerCase().replace(/\s+/g, '-');
      return this.badgeColors[collectionHandle] || this.defaultBadgeColor;
    }
    return this.defaultBadgeColor;
  }

  formatMoney(value) {
  // Se o preço aparece 100x menor, o dado recebido já é o valor final.
  // Convertemos para número e formatamos como moeda brasileira (R$).
  const price = parseFloat(value); 
  
  return price.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
}

  renderEmpty() {
    this.predictiveSearchResults.innerHTML = `
      <div class="predictive-search--empty">
        <p>Nenhum curso encontrado para "${this.escapeHtml(this.searchTerm)}"</p>
      </div>
    `;
    this.open();
  }

  open() {
    this.predictiveSearchResults.style.display = 'block';
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.predictiveSearchResults.style.display = 'none';
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.innerHTML = `
      <div class="predictive-search__loading-state">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
    `;
  }

  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearchCourse);

/**
 * Inicialização das cores de badge
 * Este código deve ser chamado no theme.liquid ou header.liquid
 */
document.addEventListener('DOMContentLoaded', function() {
  // As cores serão injetadas via Liquid no theme.liquid
  // Exemplo de como você deve adicionar no theme.liquid:
  /*
  <script>
    window.badgeColors = {
      {% for i in (1..10) %}
        {% assign coll_setting_id = 'badge_coll_' | append: i %}
        {% assign color_setting_id = 'badge_color_' | append: i %}
        {% assign selected_coll = settings[coll_setting_id] %}
        {% assign selected_color = settings[color_setting_id] %}
        {% if selected_coll != blank %}
          "{{ selected_coll.handle }}": "{{ selected_color }}",
        {% endif %}
      {% endfor %}
    };
    window.defaultBadgeColor = "{{ settings.badge_default_color | default: '#FF6B00' }}";
  </script>
  */
});
